/**
 * One standalone card surface, as three declared regions.
 *
 * The regions are the point. `auto 1fr auto` is what makes a short card and a
 * tall card reach the same height in a rail — the body absorbs the difference,
 * so no card leaves a void and no card needs a minimum. That is why there is
 * no `minHeight` prop here at any density: a floor declared on the card is
 * exactly how one rail constant travelled into a card grid and produced inert
 * empty plates.
 *
 * The shell publishes its core's rung, so everything a family nests inside it
 * lands on the ladder without asking.
 *
 * Both surfaces are painted by `Cresultcard.css` from custom properties this
 * component sets, rather than by inline `background-*` declarations. The values
 * are the same and still live in the token module; what changes is that a family
 * stylesheet can now light a hovered card by ordinary specificity. An inline
 * background can only be outranked with `!important`, and a family that had to
 * write one was carrying a workaround for this file.
 */
"use client";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { CresultcardRadiusProvider } from "./cresultcard.radius";
import {
  CRC_CARD_BACKGROUND_COLOR,
  CRC_CARD_BACKGROUND_IMAGE,
  CRC_CARD_SHADOW,
  CRC_CORE_BACKGROUND_COLOR,
  CRC_CORE_BACKGROUND_IMAGE,
  CRC_CORE_SHADOW,
  CRC_R_CARD,
  CRC_R_CORE,
  CRC_TEXT,
  CRC_TRAY_PAD,
} from "./cresultcard.tokens";
import { CresultcardDensity } from "./cresultcard.types";
import "./Cresultcard.css";

/** The card's own bezel is one rung thinner than the tray's. */
const SHELL_PAD = 3;

/**
 * Only the rail stretches its cards. A standalone card is content height, and
 * a card grid is `align-items: start`, so ragged bottoms are the honest
 * outcome of content-height cards rather than something to paper over with a
 * floor.
 */
const DENSITY_HEIGHT: Record<CresultcardDensity, CSSProperties["height"]> = {
  [CresultcardDensity.Rail]: "100%",
  [CresultcardDensity.Card]: "auto",
  [CresultcardDensity.Detail]: "auto",
};

/**
 * The two surfaces, handed to the stylesheet as custom properties.
 *
 * Declared as a type rather than cast, because a style object with an unknown
 * key is exactly the shape a typo hides in.
 */
interface CresultcardShellSurfaceStyle extends CSSProperties {
  readonly "--crc-shell-background-color": string;
  readonly "--crc-shell-background-image": string;
  readonly "--crc-shell-shadow": string;
  readonly "--crc-core-background-color": string;
  readonly "--crc-core-background-image": string;
  readonly "--crc-core-shadow": string;
}

interface CresultcardShellProps {
  /** The family root plus its variant modifier, e.g. `chotelcard chotelcard--poster`. */
  readonly className?: string;
  /** The family's variant member. Stamped for the audit tooling to read. */
  readonly variant: string;
  readonly density: CresultcardDensity;
  /** Intrinsic. Identity, the comparison figure, the media frame. */
  readonly head?: ReactNode;
  /** The only region that flexes. */
  readonly body: ReactNode;
  /** Intrinsic. Secondary facts and the action bar. */
  readonly foot?: ReactNode;
  /** Marks the card as the thing a quiet click zone opens. */
  readonly openable?: boolean;
}

export function CresultcardShell({
  className,
  variant,
  density,
  head,
  body,
  foot,
  openable = false,
}: CresultcardShellProps): ReactElement {
  const surfaceStyle: CresultcardShellSurfaceStyle = {
    width: "100%",
    height: DENSITY_HEIGHT[density],
    minWidth: 0,
    boxSizing: "border-box",
    padding: SHELL_PAD,
    overflow: "hidden",
    borderRadius: CRC_R_CARD,
    color: CRC_TEXT,
    "--crc-shell-background-color": CRC_CARD_BACKGROUND_COLOR,
    "--crc-shell-background-image": CRC_CARD_BACKGROUND_IMAGE,
    "--crc-shell-shadow": CRC_CARD_SHADOW,
    "--crc-core-background-color": CRC_CORE_BACKGROUND_COLOR,
    "--crc-core-background-image": CRC_CORE_BACKGROUND_IMAGE,
    "--crc-core-shadow": CRC_CORE_SHADOW,
  };

  return (
    <article
      className={
        className == null
          ? "cresultcard cresultcard__shell"
          : `cresultcard cresultcard__shell ${className}`
      }
      data-el="Cresultcard/Shell/Root"
      data-crc-open={openable ? true : undefined}
      data-crc-variant={variant}
      style={surfaceStyle}
    >
      <div
        className="cresultcard__shell-core"
        data-el="Cresultcard/Shell/Panel"
        style={{
          display: "grid",
          gridTemplateRows: "auto 1fr auto",
          minWidth: 0,
          height: "100%",
          overflow: "hidden",
          borderRadius: CRC_R_CORE,
        }}
      >
        <CresultcardRadiusProvider radius={CRC_R_CORE}>
          <div
            data-el="Cresultcard/Shell/Header"
            style={{ minWidth: 0 }}
          >
            {head}
          </div>
          <div
            data-el="Cresultcard/Shell/Surface"
            style={{ display: "flex", flexDirection: "column", minWidth: 0 }}
          >
            {body}
          </div>
          <div
            data-el="Cresultcard/Shell/Footer"
            style={{ minWidth: 0 }}
          >
            {foot}
          </div>
        </CresultcardRadiusProvider>
      </div>
    </article>
  );
}

/** The inset a family's own padding starts from, so two families agree. */
export const CRC_SHELL_INSET = CRC_TRAY_PAD + SHELL_PAD;
